import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n';
import expenseService from '../../services/expenseService';
import FinanceCategoryDetail from './FinanceCategoryDetail';
import { annularSectorPath, fullRingPath } from '../energy/PizzaChart';
import {
    barHeightPct,
    barLayout,
    formatAxisValue,
    labelIndices,
    labelStride,
    niceTicks,
    scrollTargetFor,
} from '../../utils/dailyEnergyChart';
import {
    OTHER_KEY,
    RANGE_PRESETS,
    categoryColors,
    dayGroupingAllowed,
    defaultGranularity,
    foldCategories,
    isValidRange,
    pieAngles,
    periodToDate,
    pieCategoriesFor,
    rangeForPreset,
} from '../../utils/financeCharts';

// ─────────────────────────────────────────────────────────────────────────────
// Finance Visualization Mode (#442).
//
// Two charts over the OPEN book (or Unassigned) for a chosen range:
//   • a bar chart of totals per day or per month (expenses by default, income on
//     request — two separate views of one measure, never a dual axis);
//   • a pie by the user's own categories that follows the same Expenses/Income
//     toggle as the bars (#482): "Spending by category" or "Income by category".
//
// Every number comes from GET /api/expenses/analytics, which converts each row
// into the book's effective currency on the server (the #196/#250 rule). This
// component never adds two amounts together — a mixed-currency book cannot be
// summed 1:1 here because nothing here sums.
//
// No charting library: bars reuse the Energy HUB's geometry
// (`utils/dailyEnergyChart`) and the pie reuses PizzaChart's annular sectors.
// Responsive by CONTAINER width (ResizeObserver), never a viewport breakpoint —
// the Finance panel is often a narrow docked split (#142/#243).
// ─────────────────────────────────────────────────────────────────────────────

const AXIS_W = 44;
const PLOT_H = 140;
const LABEL_H = 20;
const FALLBACK_W = 480;
/** Below this width the pie stacks above its legend. */
const PIE_STACK_WIDTH = 520;

// Single-series bar colours: categorical slot 1 (expenses) and slot 3 (income)
// from the validated palette in utils/financeCharts — identity colours, not the
// reserved status red/green.
const SERIES_COLOR = { expense: '#3987e5', income: '#199e70' };

const CX = 80;
const CY = 80;

const presetLabel = (t, p) => ({
    '10d': t('finVizRange10d', 'Last 10 days'),
    '15d': t('finVizRange15d', 'Last 15 days'),
    '30d': t('finVizRange30d', 'Last 30 days'),
    month: t('finVizRangeMonth', 'This month'),
    '12m': t('finVizRange12m', 'Last 12 months'),
}[p]);

const chip = (active) => `pai-tap-row px-3 py-1.5 text-[13px] font-semibold rounded-seg border transition-colors min-w-0 max-w-full truncate ${
    active
        ? 'bg-white/[0.1] text-white border-pai'
        : 'text-pai-meta border-pai-subtle hover:text-white hover:bg-white/[0.06]'
}`;

const FinanceCharts = ({ bookKey, displayCurrency, formatAmount, revision }) => {
    const { t, language } = useLanguage();

    // ── Range + grouping ─────────────────────────────────────────────────────
    const [preset, setPreset] = useState('30d');
    const [range, setRange] = useState(() => rangeForPreset('30d'));
    const [custom, setCustom] = useState(() => rangeForPreset('30d'));
    const [granularity, setGranularity] = useState('day');
    const [direction, setDirection] = useState('expense');

    const choosePreset = (p) => {
        const r = rangeForPreset(p);
        setPreset(p);
        setRange(r);
        setGranularity(defaultGranularity(r.start, r.end));
    };
    const chooseCustom = () => {
        setPreset('custom');
        setCustom(range);
    };
    const setCustomBound = (field, v) => {
        const next = { ...custom, [field]: v };
        setCustom(next);
        if (isValidRange(next.start, next.end)) {
            setRange(next);
            if (!dayGroupingAllowed(next.start, next.end)) setGranularity('month');
        }
    };
    const customInvalid = preset === 'custom' && !isValidRange(custom.start, custom.end);
    const dayAllowed = dayGroupingAllowed(range.start, range.end);
    const effectiveGranularity = dayAllowed ? granularity : 'month';

    // ── Fetch (last-ISSUED wins, the #294 rule) ──────────────────────────────
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const reqRef = useRef(0);

    const load = useCallback(async () => {
        const seq = ++reqRef.current;
        setLoading(true);
        setError(null);
        try {
            const res = await expenseService.analytics({
                bookId: bookKey,
                startDate: range.start,
                endDate: range.end,
                granularity: effectiveGranularity,
                displayCurrency,
            });
            if (seq !== reqRef.current) return;
            setData(res);
        } catch (err) {
            if (seq !== reqRef.current) return;
            setError(err?.message || 'error');
        } finally {
            if (seq === reqRef.current) setLoading(false);
        }
    }, [bookKey, range.start, range.end, effectiveGranularity, displayCurrency]);

    // `revision` changes whenever the ledger reloads (a transaction was added,
    // moved, deleted, or arrived from a channel) so the charts follow it.
    useEffect(() => { load(); }, [load, revision]);

    // ── Container width ──────────────────────────────────────────────────────
    const [rootEl, setRootEl] = useState(null);
    const [rootW, setRootW] = useState(0);
    const [plotEl, setPlotEl] = useState(null);
    const [plotW, setPlotW] = useState(0);
    useEffect(() => {
        const els = [[rootEl, setRootW], [plotEl, setPlotW]].filter(([el]) => el);
        if (!els.length) return undefined;
        const measure = () => els.forEach(([el, set]) => set(el.getBoundingClientRect().width || 0));
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        els.forEach(([el]) => ro.observe(el));
        return () => ro.disconnect();
    }, [rootEl, plotEl]);
    const pieStacked = (rootW || FALLBACK_W) < PIE_STACK_WIDTH;

    // ── Series ───────────────────────────────────────────────────────────────
    const series = useMemo(() => data?.series || [], [data]);
    const currency = data?.currency || displayCurrency;
    const values = useMemo(() => series.map((p) => Math.max(0, Number(p?.[direction]) || 0)), [series, direction]);
    const peak = values.reduce((m, v) => (v > m ? v : m), 0);
    const { ticks, max: scaleMax } = useMemo(() => niceTicks(peak), [peak]);
    const layout = barLayout(series.length, Math.max(0, (plotW || FALLBACK_W) - AXIS_W));
    const stride = labelStride(layout.slot, data?.granularity === 'month' ? 52 : 44);
    const labelled = useMemo(() => new Set(labelIndices(series.length, stride)), [series.length, stride]);
    const scrollerRef = useRef(null);
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || !layout.scrolls) return;
        el.scrollLeft = scrollTargetFor(series.length - 1, layout.slot, layout.plotWidth, el.clientWidth);
    }, [data, layout.scrolls]); // eslint-disable-line react-hooks/exhaustive-deps

    const locale = language === 'es' ? 'es' : 'en';
    const periodFmt = useMemo(() => {
        const opts = data?.granularity === 'month'
            ? { month: 'short', year: '2-digit' }
            : { month: 'short', day: 'numeric' };
        try { return new Intl.DateTimeFormat(locale, opts); } catch { return null; }
    }, [locale, data?.granularity]);
    const longFmt = useMemo(() => {
        const opts = data?.granularity === 'month'
            ? { month: 'long', year: 'numeric' }
            : { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
        try { return new Intl.DateTimeFormat(locale, opts); } catch { return null; }
    }, [locale, data?.granularity]);
    const fmtPeriod = (f, period) => {
        const d = periodToDate(period);
        return f && d ? f.format(d) : period;
    };

    const [activeBar, setActiveBar] = useState(-1);
    const activePeriod = activeBar >= 0 ? series[activeBar] : null;
    const rangeTotal = direction === 'income' ? data?.total_income : data?.total_expense;

    // ── Pie ──────────────────────────────────────────────────────────────────
    // #482: the pie follows the Expenses/Income toggle — its categories, totals,
    // title and empty state all come from the active direction's list.
    const pieList = useMemo(() => pieCategoriesFor(data, direction), [data, direction]);
    const slices = useMemo(() => foldCategories(pieList, undefined, 'amount'), [pieList]);
    const colors = useMemo(() => categoryColors(pieList.map((c) => c.category)), [pieList]);
    const angles = useMemo(() => pieAngles(slices.map((s) => s.amount)), [slices]);
    const pieTitle = direction === 'income'
        ? t('finVizIncomeByCategory', 'Income by category')
        : t('finVizByCategory', 'Spending by category');
    const pieTotal = direction === 'income' ? data?.total_income : data?.total_expense;
    const [activeSlice, setActiveSlice] = useState(null);
    // A hovered/focused slice belongs to the previous direction's pie.
    useEffect(() => { setActiveSlice(null); }, [direction]);
    const categoryName = (c) => {
        if (c === OTHER_KEY) return t('finVizOther', 'Other');
        if (c === 'Uncategorized') return t('finVizUncategorized', 'Uncategorized');
        return c;
    };
    const fmtPct = (p) => `${Number(p || 0).toFixed(1)}%`;
    const active = activeSlice ? slices.find((s) => s.category === activeSlice) : null;

    // #483: clicking a slice (or its legend row) opens the transactions behind
    // it. The detail is pinned to the scope the pie was DRAWN from (the
    // payload's own range, the direction at click time), and closing it only
    // clears this state — the charts are neither re-fetched nor re-laid out.
    const [detailSlice, setDetailSlice] = useState(null);
    const openDetail = (s) => setDetailSlice({ ...s, direction });
    const closeDetail = useCallback(() => setDetailSlice(null), []);
    const onSliceKey = (e, s) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            openDetail(s);
        }
    };

    const hasRows = (data?.expense_count || 0) + (data?.income_count || 0) > 0;

    return (
        <div
            ref={setRootEl}
            data-testid="finance-charts"
            data-loading={loading ? 'true' : 'false'}
            data-currency={currency || ''}
            className="space-y-3.5 min-w-0 max-w-full"
        >
            {/* Controls — one row of filters above the charts, wrapping when narrow. */}
            <div className="flex flex-wrap items-center gap-1.5 min-w-0 max-w-full" data-testid="finance-range" data-preset={preset}>
                {RANGE_PRESETS.map((p) => (
                    <button
                        key={p}
                        type="button"
                        data-testid={`finance-range-${p}`}
                        aria-pressed={preset === p}
                        onClick={() => choosePreset(p)}
                        className={chip(preset === p)}
                    >
                        {presetLabel(t, p)}
                    </button>
                ))}
                <button
                    type="button"
                    data-testid="finance-range-custom"
                    aria-pressed={preset === 'custom'}
                    onClick={chooseCustom}
                    className={chip(preset === 'custom')}
                >
                    {t('finVizRangeCustom', 'Custom')}
                </button>
                {preset === 'custom' && (
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                        <input
                            type="date"
                            data-testid="finance-range-start"
                            aria-label={t('finVizFrom', 'From')}
                            value={custom.start || ''}
                            max={custom.end || undefined}
                            onChange={(e) => setCustomBound('start', e.target.value)}
                            className="pai-input px-2.5 py-1.5 text-[13px] tnum min-w-0 [color-scheme:dark]"
                        />
                        <span className="text-[13px] text-pai-faint">–</span>
                        <input
                            type="date"
                            data-testid="finance-range-end"
                            aria-label={t('finVizTo', 'To')}
                            value={custom.end || ''}
                            min={custom.start || undefined}
                            onChange={(e) => setCustomBound('end', e.target.value)}
                            className="pai-input px-2.5 py-1.5 text-[13px] tnum min-w-0 [color-scheme:dark]"
                        />
                    </div>
                )}
            </div>
            {customInvalid && (
                <p data-testid="finance-range-invalid" className="text-[12.5px] text-pai-amber break-words">
                    {t('finVizInvalidRange', 'Choose a start date on or before the end date.')}
                </p>
            )}

            {error && (
                <div data-testid="finance-charts-error" className="border border-[rgba(240,68,56,0.32)] bg-[rgba(240,68,56,0.04)] rounded-row px-4 py-3 text-[13.5px] text-pai-red break-words">
                    {t('finVizError', 'Could not load the charts.')}{' '}
                    <button type="button" onClick={load} className="underline hover:text-white">
                        {t('commonRetry', 'Retry')}
                    </button>
                </div>
            )}

            {loading && !data && !error && (
                <div className="flex items-center gap-2 py-8 text-[13.5px] text-pai-meta">
                    <Loader2 size={14} className="animate-spin" />
                    {t('finLoadingEllipsis', 'Loading…')}
                </div>
            )}

            {/* #449: rows with no date count in the ledger balance but can sit
                on no time axis — say so, with their total, so the charts and the
                Net Balance reconcile instead of silently disagreeing. */}
            {data && !error && (data.undated?.count || 0) > 0 && (
                <p
                    data-testid="finance-charts-undated"
                    data-count={data.undated.count}
                    data-expense={data.undated.expense_total}
                    data-income={data.undated.income_total}
                    className="text-[12.5px] text-pai-meta break-words"
                >
                    {t('finVizUndated', '{n} transaction(s) have no date, so they are not in these charts ({amount} in expenses). Add a date to include them.', {
                        n: data.undated.count,
                        amount: formatAmount(data.undated.expense_total, data.currency || displayCurrency),
                    })}
                </p>
            )}

            {data && !error && !hasRows && (
                <div data-testid="finance-charts-empty" className="pai-card text-center py-12 px-6">
                    <p className="text-[17px] font-bold text-white/70">{t('finVizEmpty', 'No transactions in this period.')}</p>
                    <p className="text-[14px] text-white/40 mt-1.5 break-words">
                        {t('finVizEmptyHint', 'Pick a longer range, or log a transaction to see it here.')}
                    </p>
                </div>
            )}

            {data && !error && hasRows && (
                <>
                    {data.unconverted_count > 0 && (
                        <p
                            data-testid="finance-charts-unconverted"
                            className="text-[12.5px] text-pai-amber break-words"
                        >
                            {t('finVizUnconverted', '{n} transaction(s) left out: no exchange rate was available.', { n: data.unconverted_count })}
                        </p>
                    )}

                    {/* ── Bar chart: totals per period ─────────────────────── */}
                    <section
                        data-testid="finance-series-chart"
                        data-granularity={data.granularity}
                        data-direction={direction}
                        data-bars={series.length}
                        data-scrolls={layout.scrolls ? 'true' : 'false'}
                        aria-label={direction === 'income'
                            ? t('finVizIncomeOverTime', 'Income over time')
                            : t('finVizSpendingOverTime', 'Spending over time')}
                        className={`pai-card px-5 py-4 min-w-0 max-w-full transition-opacity ${loading ? 'opacity-60' : ''}`}
                    >
                        <header className="flex flex-wrap items-center justify-between gap-2 min-w-0">
                            <span className="pai-eyebrow truncate min-w-0">
                                {direction === 'income'
                                    ? t('finVizIncomeOverTime', 'Income over time')
                                    : t('finVizSpendingOverTime', 'Spending over time')}
                            </span>
                            <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                                <div className="flex items-center gap-1 p-1 rounded-ctl border border-pai-subtle bg-white/[0.03]">
                                    {['expense', 'income'].map((d) => (
                                        <button
                                            key={d}
                                            type="button"
                                            data-testid={`finance-series-${d}`}
                                            aria-pressed={direction === d}
                                            onClick={() => setDirection(d)}
                                            className={`pai-tap-row px-2.5 py-1 text-[12.5px] font-semibold rounded-seg transition-colors ${
                                                direction === d ? 'bg-white/[0.1] text-white' : 'text-pai-meta hover:text-white'
                                            }`}
                                        >
                                            {d === 'income' ? t('finIncomePlural', 'Income') : t('finExpensesPlural', 'Expenses')}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-1 p-1 rounded-ctl border border-pai-subtle bg-white/[0.03]">
                                    {['day', 'month'].map((g) => (
                                        <button
                                            key={g}
                                            type="button"
                                            data-testid={`finance-granularity-${g}`}
                                            aria-pressed={effectiveGranularity === g}
                                            disabled={g === 'day' && !dayAllowed}
                                            title={g === 'day' && !dayAllowed ? t('finVizDayTooLong', 'Daily view is limited to 366 days.') : undefined}
                                            onClick={() => setGranularity(g)}
                                            className={`pai-tap-row px-2.5 py-1 text-[12.5px] font-semibold rounded-seg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                                effectiveGranularity === g ? 'bg-white/[0.1] text-white' : 'text-pai-meta hover:text-white'
                                            }`}
                                        >
                                            {g === 'day' ? t('finVizDaily', 'Daily') : t('finVizMonthly', 'Monthly')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </header>

                        <p
                            data-testid="finance-series-readout"
                            aria-live="polite"
                            className="mt-2 text-[12.5px] text-pai-meta tnum truncate min-w-0"
                        >
                            {activePeriod
                                ? `${fmtPeriod(longFmt, activePeriod.period)} · ${formatAmount(activePeriod[direction], currency)}`
                                : `${t('finVizRangeTotal', 'Range total')}: ${formatAmount(rangeTotal ?? 0, currency)}`}
                        </p>

                        <div ref={setPlotEl} className="mt-3 flex min-w-0 max-w-full">
                            <div aria-hidden="true" className="relative flex-shrink-0" style={{ width: AXIS_W, height: PLOT_H }}>
                                {ticks.map((v) => (
                                    <span
                                        key={v}
                                        className="absolute right-2 text-[10.5px] leading-none text-pai-faint tnum"
                                        style={{ bottom: `calc(${barHeightPct(v, scaleMax) || 0}% - 5px)` }}
                                    >
                                        {formatAxisValue(v)}
                                    </span>
                                ))}
                            </div>
                            <div
                                ref={scrollerRef}
                                data-testid="finance-series-scroller"
                                className="relative flex-1 min-w-0 overflow-x-auto overflow-y-hidden"
                            >
                                <div className="relative" style={{ width: layout.plotWidth }}>
                                    <div className="relative" style={{ height: PLOT_H }}>
                                        {ticks.map((v) => (
                                            <span
                                                key={v}
                                                aria-hidden="true"
                                                className={`absolute left-0 right-0 border-t ${v === 0 ? 'border-pai' : 'border-pai-hairline'}`}
                                                style={{ bottom: `${barHeightPct(v, scaleMax)}%` }}
                                            />
                                        ))}
                                        <div className="absolute inset-0 flex items-end">
                                            {series.map((p, i) => {
                                                const label = t('finVizBarLabel', '{period}: {amount}', {
                                                    period: fmtPeriod(longFmt, p.period),
                                                    amount: formatAmount(p[direction], currency),
                                                });
                                                const h = barHeightPct(values[i], scaleMax);
                                                return (
                                                    <div
                                                        key={p.period}
                                                        role="img"
                                                        tabIndex={0}
                                                        data-testid="finance-series-bar"
                                                        data-period={p.period}
                                                        data-value={p[direction]}
                                                        data-expense={p.expense}
                                                        data-income={p.income}
                                                        aria-label={label}
                                                        title={label}
                                                        onMouseEnter={() => setActiveBar(i)}
                                                        onMouseLeave={() => setActiveBar(-1)}
                                                        onFocus={() => setActiveBar(i)}
                                                        onBlur={() => setActiveBar(-1)}
                                                        className="pai-no-hit-slop group relative h-full flex items-end justify-center flex-shrink-0 focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/80"
                                                        style={{ width: layout.slot }}
                                                    >
                                                        <span
                                                            aria-hidden="true"
                                                            className={`block rounded-t-[4px] transition-opacity ${
                                                                activeBar === i ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'
                                                            }`}
                                                            style={{
                                                                width: layout.barWidth,
                                                                height: h > 0 ? `${h}%` : 0,
                                                                background: SERIES_COLOR[direction],
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div aria-hidden="true" className="relative overflow-hidden" style={{ height: LABEL_H }}>
                                        {series.map((p, i) => (labelled.has(i) ? (
                                            <span
                                                key={p.period}
                                                className="absolute top-[8px] text-[10.5px] leading-none whitespace-nowrap overflow-hidden tnum text-pai-faint"
                                                style={{ left: i * layout.slot, width: stride * layout.slot }}
                                            >
                                                {fmtPeriod(periodFmt, p.period)}
                                            </span>
                                        ) : null))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── Pie: spending or income by category (#482) ───────── */}
                    <section
                        data-testid="finance-pie-chart"
                        data-direction={direction}
                        data-slices={slices.length}
                        aria-label={pieTitle}
                        className={`pai-card px-5 py-4 min-w-0 max-w-full transition-opacity ${loading ? 'opacity-60' : ''}`}
                    >
                        <span data-testid="finance-pie-title" className="pai-eyebrow block truncate">{pieTitle}</span>
                        {slices.length === 0 ? (
                            <p data-testid="finance-pie-empty" className="mt-3 text-[13px] text-pai-meta break-words">
                                {direction === 'income'
                                    ? t('finVizNoIncome', 'No income in this period.')
                                    : t('finVizNoSpending', 'No spending in this period.')}
                            </p>
                        ) : (
                            <div className={`mt-3 flex ${pieStacked ? 'flex-col items-center' : 'flex-row items-center'} gap-5 min-w-0 max-w-full`}>
                                <div className="w-40 h-40 flex-shrink-0">
                                    <svg viewBox="0 0 160 160" width="100%" height="100%" role="img" aria-label={pieTitle}>
                                        <circle cx={CX} cy={CY} r={60} fill="none" strokeWidth={28} stroke="rgb(255 255 255 / 0.08)" />
                                        {slices.map((s, i) => {
                                            const a = angles[i];
                                            if (!a) return null;
                                            const full = a.sweep >= 359.999;
                                            const label = t('finVizSliceLabel', '{category}: {amount} ({pct})', {
                                                category: categoryName(s.category),
                                                amount: formatAmount(s.amount, currency),
                                                pct: fmtPct(s.pct),
                                            });
                                            return (
                                                <path
                                                    key={s.category}
                                                    data-testid="finance-pie-slice"
                                                    data-category={s.category}
                                                    data-value={s.amount}
                                                    data-pct={s.pct}
                                                    d={full ? fullRingPath() : annularSectorPath(a.start, a.end)}
                                                    fillRule={full ? 'evenodd' : undefined}
                                                    fill={colors[s.category] || colors[OTHER_KEY]}
                                                    stroke="rgb(11 15 20 / 0.55)"
                                                    strokeWidth={2}
                                                    tabIndex={0}
                                                    role="button"
                                                    aria-haspopup="dialog"
                                                    aria-label={label}
                                                    opacity={activeSlice && activeSlice !== s.category ? 0.45 : 1}
                                                    onMouseEnter={() => setActiveSlice(s.category)}
                                                    onMouseLeave={() => setActiveSlice(null)}
                                                    onFocus={() => setActiveSlice(s.category)}
                                                    onBlur={() => setActiveSlice(null)}
                                                    onClick={() => openDetail(s)}
                                                    onKeyDown={(e) => onSliceKey(e, s)}
                                                    className="cursor-pointer focus:outline-none transition-opacity"
                                                >
                                                    <title>{label}</title>
                                                </path>
                                            );
                                        })}
                                        <text x={CX} y={CY - 5} textAnchor="middle" fill="rgb(255 255 255 / 0.5)" pointerEvents="none" style={{ fontSize: 9 }}>
                                            {active ? categoryName(active.category) : t('finVizTotalLabel', 'Total')}
                                        </text>
                                        <text
                                            x={CX}
                                            y={CY + 12}
                                            textAnchor="middle"
                                            data-testid="finance-pie-total"
                                            pointerEvents="none"
                                            fill="#ffffff"
                                            style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '-0.02em' }}
                                        >
                                            {active ? fmtPct(active.pct) : formatAmount(pieTotal ?? 0, currency)}
                                        </text>
                                    </svg>
                                </div>
                                <ul className={`min-w-0 ${pieStacked ? 'w-full' : 'flex-1'} space-y-1`}>
                                    {slices.map((s) => (
                                        <li
                                            key={s.category}
                                            data-testid="finance-pie-legend-row"
                                            data-category={s.category}
                                            role="button"
                                            tabIndex={0}
                                            aria-haspopup="dialog"
                                            aria-label={t('finCatDetailOpen', 'Show the transactions in {category}', { category: categoryName(s.category) })}
                                            onMouseEnter={() => setActiveSlice(s.category)}
                                            onMouseLeave={() => setActiveSlice(null)}
                                            onFocus={() => setActiveSlice(s.category)}
                                            onBlur={() => setActiveSlice(null)}
                                            onClick={() => openDetail(s)}
                                            onKeyDown={(e) => onSliceKey(e, s)}
                                            className={`pai-tap-row cursor-pointer flex items-center gap-[11px] px-2 py-1.5 rounded-seg min-w-0 max-w-full transition-colors hover:bg-white/[0.07] focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/60 ${
                                                activeSlice === s.category ? 'bg-white/[0.07]' : ''
                                            }`}
                                        >
                                            <span
                                                aria-hidden="true"
                                                className="w-[11px] h-[11px] rounded-[3px] flex-shrink-0"
                                                style={{ background: colors[s.category] || colors[OTHER_KEY] }}
                                            />
                                            <span className="text-[13.5px] font-medium text-pai-secondary truncate min-w-0 flex-1" title={categoryName(s.category)}>
                                                {categoryName(s.category)}
                                            </span>
                                            <span className="text-[13.5px] font-bold text-white tnum whitespace-nowrap flex-shrink-0">
                                                {formatAmount(s.amount, currency)}
                                            </span>
                                            <span className="text-[12.5px] text-pai-meta tnum flex-shrink-0 w-14 text-right">
                                                {fmtPct(s.pct)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>
                </>
            )}

            {detailSlice && (
                <FinanceCategoryDetail
                    slice={detailSlice}
                    label={categoryName(detailSlice.category)}
                    direction={detailSlice.direction}
                    bookKey={bookKey}
                    startDate={data?.start_date || range.start}
                    endDate={data?.end_date || range.end}
                    displayCurrency={displayCurrency}
                    currency={currency}
                    formatAmount={formatAmount}
                    onClose={closeDetail}
                />
            )}
        </div>
    );
};

export default FinanceCharts;
